import { Search, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Input } from './ui/input';
import { PageHeader } from './page-header';
import { PhotoBadge, PhotoCategory } from './photo-badge';

interface TrashPhoto {
  id: string;
  url: string;
  category: PhotoCategory;
  propertyAddress: string;
  date: string;
  aspectRatio: 'landscape' | 'square';
  roomName?: string;
}

interface TrashViewProps {
  trashedPhotos: TrashPhoto[];
  onRestore?: (photoUrl: string) => void;
}

export function TrashView({ trashedPhotos, onRestore }: TrashViewProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter photos based on search query
  const filteredPhotos = trashedPhotos.filter(
    (photo) =>
      photo.propertyAddress.toLowerCase().includes(searchQuery.toLowerCase()) ||
      photo.roomName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-full relative isolate">
      <PageHeader
        title="Kosz"
        controls={
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
            <Input
              type="text"
              placeholder="Szukaj..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-8 text-[13px] bg-background border-border"
            />
          </div>
        }
      />

      {/* Grid Container */}
      <div className="px-5 py-10">
        {filteredPhotos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <h3 className="text-[15px] font-medium mb-2">
              {searchQuery ? 'Nie znaleziono wyników' : 'Kosz jest pusty'}
            </h3>
            <p className="text-[13px] text-muted-foreground max-w-md">
              {searchQuery
                ? 'Spróbuj użyć innych słów kluczowych'
                : 'Usunięte pliki pojawią się tutaj. Po 30 dniach zostaną one usunięte bezpowrotnie.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 max-w-[1800px]">
            {filteredPhotos.map((photo) => (
              <div
                key={photo.id}
                className="group relative rounded-lg overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-all"
              >
                {/* Image Container */}
                <div
                  className={`relative ${photo.aspectRatio === 'square' ? 'aspect-square' : 'aspect-video'
                    } overflow-hidden bg-muted`}
                >
                  <img
                    src={photo.url}
                    alt={`${photo.propertyAddress} - ${photo.roomName || 'Photo'}`}
                    className="w-full h-full object-cover opacity-60 grayscale-[0.6]"
                  />

                  {/* Restore Button Overlay - Always visible on mobile, hover on desktop */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity bg-black/40">
                    <button
                      onClick={() => onRestore?.(photo.url)}
                      className="px-4 py-2 bg-white text-zinc-900 border border-zinc-200 rounded-md text-[13px] font-medium flex items-center gap-2 hover:bg-zinc-50 transition-colors shadow-sm"
                    >
                      <RotateCcw className="w-4 h-4" strokeWidth={2} />
                      Przywróć
                    </button>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-2 left-2">
                    <PhotoBadge category={photo.category} />
                  </div>
                </div>

                {/* Info */}
                <div className="p-3 border-t border-border bg-card">
                  <div className="text-[12px] font-medium truncate mb-1">
                    {photo.roomName || 'Photo'}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {photo.propertyAddress}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {photo.date}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
